<template>
  <div class="col-xs-12 col-sm-12 offset-md-1 col-md-10">
    <el-divider class="divider-green"></el-divider>

    <div class="row d-flex justify-content-between align-items-center">
      <div class="block">
        <h2>Audit Log</h2>
        <p v-show="auditLog.length > 0">
          Displaying {{ auditLog.length }} logs of {{ totalAuditLogCount }}
        </p>
      </div>
      <div class="row d-flex align-items-center">
        <div>
          <base-button @click="exportPDF()" class="btn-dark float-sm-none float-md-left">Export PDF</base-button>
        </div>
        <div style="width: 300px; padding-left:20px">
          <el-input :disabled="!this.isOrderIdAvailable" @input="searchInput()" v-model="search"
            placeholder="Type to search" />
        </div>
      </div>
    </div>
    <br />

    <div class="block">

      <br />
      <el-timeline v-if="groupedAuditLog && groupedAuditLog.length && this.isOrderIdAvailable">
        <el-timeline-item v-for="(group, index) in groupedAuditLog" :timestamp="group.logDate | formatDateTime" :key="index"
          placement="top">
          <el-card shadow="never">
            <div class="row">
              <div class="col-md-12">
                <el-table :data="group.items" role="table" aria-label="Audit Logs Table">
                  <el-table-column prop="updateBy" label="Update By" scope="col">
                  </el-table-column>
                  <el-table-column prop="action" label="Action" scope="col">
                  </el-table-column>
                  <el-table-column prop="fieldNames" label="Field Name" scope="col">
                  </el-table-column>
                  <el-table-column label="Previous Value" scope="col">
                    <template slot-scope="scope">
                      <AuditSignature v-if="(
          scope.row.fieldNames.includes('Signature') ||
          scope.row.fieldNames.includes('RequestorSignature') ||
          scope.row.fieldNames.includes('AcceptorSignature') ||
          scope.row.fieldNames.includes('ReceiverSignature') ||
          scope.row.fieldNames.includes('SupplierSignature') ||
          scope.row.fieldNames.includes('FinanceSignature')
        ) &&
          scope.row.oldValues
          " :signature-data="scope.row.oldValues" />
                      <p v-else>{{ scope.row.oldValues }}</p>
                    </template>
                  </el-table-column>
                  <el-table-column label="Current Value">
                    <template slot-scope="scope">
                      <AuditSignature v-if="(
          scope.row.fieldNames.includes('Signature') ||
          scope.row.fieldNames.includes('RequestorSignature') ||
          scope.row.fieldNames.includes('AcceptorSignature') ||
          scope.row.fieldNames.includes('ReceiverSignature') ||
          scope.row.fieldNames.includes('SupplierSignature') ||
          scope.row.fieldNames.includes('FinanceSignature')
        ) &&
          scope.row.newValues
          " :signature-data="scope.row.newValues" />
                      <p v-else>{{ scope.row.newValues }}</p>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </div>
          </el-card>
        </el-timeline-item>
        
        <!-- Show More Button -->
        <div class="text-center pt-4">
          <base-button 
            v-if="auditLog.length < totalAuditLogCount" 
            @click="loadMoreAuditLogs()" 
            type="primary"
          >Show More</base-button>
        </div>
      </el-timeline>
      
      <el-card v-if="!groupedAuditLog || !groupedAuditLog.length || !this.isOrderIdAvailable">
        <div class="text-center pt-4" style="min-height: 100px;">
          <div v-if="isLoading">
            <i class="el-icon-loading" style="font-size: 32px; color: #275d34;"></i>
            <h3 style="margin-top: 15px; color: #275d34; font-weight: 500;">Loading Audit Logs...</h3>
          </div>
          <h2 v-else>{{ auditLogLoadingText || 'No Audit Logs Available!' }}</h2>
        </div>
      </el-card>

    </div>
  </div>
</template>

<script>
import {
  Table,
  TableColumn,
  Divider,
  Tag,
  Card,
  Timeline,
  TimelineItem,
} from "element-ui";

import { mapActions, mapGetters } from "vuex";
import Util from "@/util/util";
import axios from '@/axios';
import AuditSignature from "@/components/App/signatures/AuditSignature";

export default {
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Divider.name]: Divider,
    [Tag.name]: Tag,
    [Timeline.name]: Timeline,
    [TimelineItem.name]: TimelineItem,
    [Card.name]: Card,
    AuditSignature,
  },
  data() {
    return {
      search: "",
      resultsCount: 10,
      searchDebounceTimer: null,
    };
  },
  watch: {
    async activeTab(newTab, oldTab) {
      if (newTab == "AuditLog") {
        // Only load if we don't already have data for this order
        // (avoids re-fetching when switching tabs back)
        if (this.auditLog.length === 0 || this.currentOrderId !== this.orderId) {
          await this.loadAuditLogData(1, true);
        }
      }
    },
  },
  filters: {
    formatDateTime(value) {
      if (!value) return "";
      const date = new Date(value);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
    },
  },
  computed: {
    ...mapGetters({
      auditLogLoadingText: "auditLog/auditLogLoadingText",
      auditLog: "auditLog/getAuditLog",
      totalAuditLogCount: "auditLog/getTotalAuditLogCount",
      totalUniqueActionCount: "auditLog/getTotalUniqueActionCount",
      currentPage: "auditLog/getCurrentPage",
      pageSize: "auditLog/getPageSize",
      isLoading: "auditLog/isLoading",
      currentOrderId: "auditLog/getCurrentOrderId",
      orderIdFromPreOrderDetail: "preOrderDetail/preOrder",
      activeTab: "tab/activeTab",

    }),
    // Calculate display range start (1-indexed)
    displayRangeStart() {
      if (!this.totalUniqueActionCount || this.totalUniqueActionCount === 0) return 0;
      return ((this.currentPageNumber - 1) * this.resultsCount) + 1;
    },
    // Calculate display range end
    displayRangeEnd() {
      if (!this.totalUniqueActionCount) return 0;
      const end = this.currentPageNumber * this.resultsCount;
      return Math.min(end, this.totalUniqueActionCount);
    },
    // Calculate total pages
    totalPages() {
      if (!this.totalUniqueActionCount || !this.resultsCount) return 1;
      return Math.ceil(this.totalUniqueActionCount / this.resultsCount);
    },
    // Group audit log entries by logDate to combine records from the same action
    groupedAuditLog() {
      if (!this.auditLog || !this.auditLog.length) return [];
      
      // Filter out UpdateDate field entries (case-insensitive)
      const filteredLog = this.auditLog.filter(item => {
        const fieldName = (item.fieldName || '').trim().toLowerCase();
        return fieldName !== 'updatedate' && fieldName !== 'updateddate';
      });
      
      const groups = {};
      filteredLog.forEach(item => {
        const key = item.logDate;
        if (!groups[key]) {
          groups[key] = {
            logDate: item.logDate,
            items: []
          };
        }
        groups[key].items.push(item);
      });
      
      // Return as array, sorted by logDate descending, excluding empty groups
      return Object.values(groups)
        .filter(group => group.items.length > 0)
        .sort((a, b) => new Date(b.logDate) - new Date(a.logDate));
    },
    orderIdFromRoute() {
      return parseInt(this.$route.params.id);
    },
    orderId() {
      if (this.isOrderIdAvailable && this.orderIdFromRoute !== 0) {
        return this.orderIdFromRoute;
      } else {
        return this.orderIdFromPreOrderDetail.orderId;
      }
    },
    isOrderIdAvailable() {
      return (
        this.orderIdFromRoute != 0 ||
        (this.orderIdFromRoute != 0 &&
          this.orderIdFromPreOrderDetail &&
          this.orderIdFromPreOrderDetail.orderId != 0)
      );
    },
  },
  methods: {
    ...mapActions({
      fetchAuditLog: "auditLog/fetchAuditLog",
      fetchAuditLogPagination: "auditLog/fetchAuditLogPagination"
    }),
    async loadAuditLogData(pageNumber, isclear = false) {
      if (this.isOrderIdAvailable) {
        // await  this.fetchAuditLog(this.orderId);

        // same way to pass apiInfor to the search, there is no export flag
        let apiInfo = {
          orderId: this.orderId,
          pageNumber: pageNumber ? pageNumber : 1,
          resultsPerPage: this.resultsCount,
          searchText: this.search,
          isClear: isclear
        };
        await this.fetchAuditLogPagination(apiInfo);
      }
    },
    async loadMoreAuditLogs() {
      let pageNo = this.auditLog.length / this.resultsCount + 1
      this.pageNumber = pageNo < 1 ? 1 : pageNo

      await this.loadAuditLogData(this.pageNumber)
    },

    searchInput() {
      // Debounce search - wait 300ms after user stops typing
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(async () => {
        await this.loadAuditLogData(1, true);
      }, 300);
    },

    exportPDF() {
      this.$loaderApp.addRequest();
      // Build URL - only include search parameter if it has a value
      const searchParam = this.search ? `/${encodeURIComponent(this.search)}` : '';
      axios({
        url: `/api/orderauditlogs/pdf/${this.orderId}${searchParam}`,
        method: "GET",
        responseType: "blob", // important
      }).then((response) => {
        Util.fileDownload(response.data, `Audit_Log_Summary_${this.orderId}.pdf`);
        this.$loaderApp.completeRequest();
      }).catch((error) => {
        this.$loaderApp.completeRequest();
        this.$toast.error(error);

      });
    },
  },
};
</script>

<style>
.row .search-box {
  margin-bottom: 5em;
}

.divider-green {
  background-color: #10b45a;
}

.el-table--border {
  border: 1px solid rgba(34, 42, 66, 0.2);
}

ul.el-timeline {
  padding-inline-start: 0;
}
</style>
